<template>
  <v-card tile>
    <v-card-text>
      <v-row class="mt-16">
        <v-col cols="12">
          <v-chip label color="transparent" text-color="black">
            <h2>Historial General de Consumos</h2>
          </v-chip>
        </v-col>
      </v-row>

      <!-- Filtros -->
      <v-row class="mt-2">
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="filtros.fechaDesde"
            label="Desde"
            type="date"
            dense
            prepend-icon="mdi-calendar"
            hide-details
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="filtros.fechaHasta"
            label="Hasta"
            type="date"
            dense
            prepend-icon="mdi-calendar"
            hide-details
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-autocomplete
            v-model="filtros.idProducto"
            :items="productos"
            item-text="nombre"
            item-value="idProducto"
            label="Producto"
            dense
            clearable
            prepend-icon="mdi-package-variant"
            hide-details
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="filtros.paciente"
            label="Paciente"
            dense
            clearable
            prepend-icon="mdi-account"
            hide-details
            @keypress.enter="buscarConsumos"
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row class="mt-2">
        <v-col cols="12">
          <v-btn color="primary" @click="buscarConsumos" :loading="loading">
            <v-icon left>mdi-magnify</v-icon>
            Buscar
          </v-btn>
          <v-btn color="grey" class="ml-2" @click="limpiarFiltros">
            Limpiar
          </v-btn>
          <v-btn
            color="purple"
            dark
            class="ml-2"
            @click="exportarPDF"
            :disabled="!consumos.length"
          >
            <v-icon left>mdi-file-pdf-box</v-icon>
            Exportar PDF
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Tabla -->
    <v-card-text v-if="consumos.length > 0">
      <v-data-table
        :headers="headers"
        :items="consumos"
        class="elevation-1"
        locale="es-ar"
        item-key="idConsumo"
        :items-per-page="15"
        :footer-props="{
          itemsPerPageText: 'Filas por página',
          itemsPerPageOptions: [10, 15, 30, 50],
        }"
        :loading="loading"
      >
        <template v-slot:item.total="{ item }">
          ${{ parseFloat(item.total).toFixed(2) }}
        </template>
        <template v-slot:item.cantidad="{ item }">
          {{ item.cantidad || "-" }}
        </template>
      </v-data-table>

      <!-- Totales -->
      <v-row class="mt-3">
        <v-col cols="12" class="text-right">
          <v-chip color="primary" dark label>
            Total consumos: {{ consumos.length }} | Importe total:
            ${{ totalImporte.toFixed(2) }}
          </v-chip>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text v-else-if="!loading && busquedaRealizada">
      <v-alert color="purple" dark outlined>
        No se encontraron consumos con los filtros aplicados.
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from "@/axios-auth";
import moment from "moment";
import { pdfHistorialConsumos } from "./reportesStockPDF";

export default {
  data() {
    return {
      filtros: {
        fechaDesde: moment().startOf("month").format("YYYY-MM-DD"),
        fechaHasta: moment().format("YYYY-MM-DD"),
        idProducto: null,
        paciente: "",
      },
      consumos: [],
      productos: [],
      loading: false,
      busquedaRealizada: false,
      headers: [
        { text: "Fecha", value: "fechaString" },
        { text: "Paciente", value: "paciente" },
        { text: "Descripción", value: "descripcion" },
        { text: "Cantidad", value: "cantidad" },
        { text: "Importe", value: "total" },
        { text: "Usuario", value: "usuario" },
      ],
    };
  },
  computed: {
    totalImporte() {
      return this.consumos.reduce((acc, c) => acc + (c.total || 0), 0);
    },
  },
  methods: {
    buscarConsumos() {
      this.loading = true;
      this.busquedaRealizada = true;

      const params = {
        idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
        fechaDesde: this.filtros.fechaDesde || null,
        fechaHasta: this.filtros.fechaHasta || null,
        idProducto: this.filtros.idProducto || 0,
        paciente: this.filtros.paciente || "%",
      };

      axios
        .get("/Caja/BuscarConsumosGeneral", { params })
        .then((response) => {
          this.consumos = response.data || [];
        })
        .catch(() => {
          this.consumos = [];
          this.$swal.fire({
            icon: "warning",
            title: "No se pudieron obtener los consumos.",
            showConfirmButton: false,
            background: "#ececec",
            timer: 2500,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    cargarProductos() {
      axios
        .get("/Producto/GetProductos", {
          params: {
            idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
            estado: "Activo",
            nombre: "%",
            codigo: "%",
            idCategoria: 0,
          },
        })
        .then((response) => {
          this.productos = response.data || [];
        })
        .catch(() => {
          this.productos = [];
        });
    },

    limpiarFiltros() {
      this.filtros = {
        fechaDesde: moment().startOf("month").format("YYYY-MM-DD"),
        fechaHasta: moment().format("YYYY-MM-DD"),
        idProducto: null,
        paciente: "",
      };
      this.consumos = [];
      this.busquedaRealizada = false;
    },

    exportarPDF() {
      const nombreInstitucion =
        this.$store.state.usuario.institucion.nombreInstitucion
          .toString()
          .trim();

      const productoNombre = this.filtros.idProducto
        ? (this.productos.find((p) => p.idProducto === this.filtros.idProducto) || {}).nombre
        : null;

      pdfHistorialConsumos(this.consumos, nombreInstitucion, {
        fechaDesde: this.filtros.fechaDesde,
        fechaHasta: this.filtros.fechaHasta,
        producto: productoNombre,
        paciente: this.filtros.paciente && this.filtros.paciente !== "%" ? this.filtros.paciente : null,
      });
    },
  },
  created() {
    this.cargarProductos();
    this.buscarConsumos();
  },
};
</script>
